import React from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jem-open/jem-ui';
import type { PendingSummary } from './types';

interface CreatePaymentDialogProps {
  pendingSummary: PendingSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}

const formatCurrency = (amount: string): string => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return `R${n.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Confirms the current outstanding pool (count, total, provider) before
// crystallising it into a payment. Runs on demand at any time — there is no
// month or "iTransact" framing here (ENG-4670).
const CreatePaymentDialog = ({
  pendingSummary,
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: CreatePaymentDialogProps) => {
  if (!pendingSummary) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create payment</DialogTitle>
          <DialogDescription>
            Submit every payable outstanding savings transaction to{' '}
            {pendingSummary.provider} as one payment. Lines with no Franc
            account are excluded from the total and the payload. This records an
            immutable payment and marks the included transactions as paid. This
            action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
          <dl className="space-y-2 rounded-md bg-slate-50 p-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Provider</dt>
              <dd className="font-medium">{pendingSummary.provider}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Transactions</dt>
              <dd className="font-medium">{pendingSummary.transactionCount}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Batches</dt>
              <dd className="font-medium">{pendingSummary.batchCount}</dd>
            </div>
            {pendingSummary.unresolvedCount > 0 && (
              <div className="flex justify-between text-greyscale-text-caption">
                <dt>Unresolved (excluded)</dt>
                <dd>
                  {pendingSummary.unresolvedCount} ·{' '}
                  {formatCurrency(pendingSummary.unresolvedAmount)}
                </dd>
              </div>
            )}
            <div className="flex justify-between border-t pt-2">
              <dt className="font-semibold">Total to pay</dt>
              <dd className="font-semibold">
                {formatCurrency(pendingSummary.totalAmount)}
              </dd>
            </div>
          </dl>

          <Alert variant="warning">
            <AlertTitle>The provider will begin processing</AlertTitle>
            <AlertDescription>
              Download and review the outstanding pool with finance before
              creating the payment. Lines with no provider account are excluded
              from this payment.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onConfirm} loading={isSubmitting}>
            Confirm &amp; Pay
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePaymentDialog;
